import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useDictionary, usePage, useViewport, useAuth } from '@/src/app/hooks';
import {
    AssessmentBoxes,
    ResourceBoxes,
    DropdownArrow,
    type UnitDTO,
    type ResourceHeaderDTO,
    type ResourceItemDTO,
} from '@/src/ui';
import { filterSlidesData } from '@/src/utils';
import LessonList from './LessonList';
import LessonNav from './LessonNav';
import SingleLesson from './SingleLesson';

interface CollapsibleUnitProps {
    unit: UnitDTO;
    loading: boolean;
    error: string;
    assessmentsData: ResourceHeaderDTO[] | null;
    resourcesData: ResourceHeaderDTO[] | null;
    selectedUnit: number | null;
    selectedLesson: number | null;
    selectedTab: 'lessons' | 'assessments' | 'resources' | null;
    setSelectedUnit: (unit: number | null) => void;
    setSelectedLesson: (lesson: number | null) => void;
    setSelectedTab: (tab: 'lessons' | 'assessments' | 'resources' | null) => void;
    index: number;
}

const CollapsibleUnit: React.FC<CollapsibleUnitProps> = ({
    unit,
    loading,
    error,
    assessmentsData,
    resourcesData,
    selectedUnit,
    selectedLesson,
    selectedTab,
    setSelectedUnit,
    setSelectedLesson,
    setSelectedTab,
    index,
}) => {
    document.title = 'All Lessons';
    const { translate } = useDictionary();
    const { pageGrade, setPageGrade } = usePage();
    const { isMobile } = useViewport();
    const { user, addMyLesson, removeMyLesson } = useAuth();
    const myLessons = user ? user.myLessons : [];

    useEffect(() => {
        if (!isMobile && selectedTab === null) {
            setSelectedTab('lessons');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobile]);

    const params = useParams();
    const grade = params.grade || pageGrade;
    if (grade !== pageGrade) {
        setPageGrade(grade);
    }

    const currentUnit = index + 1;
    const isUnitSelected = selectedUnit === currentUnit;
    const hideUnit = selectedLesson !== null && selectedUnit !== currentUnit;

    const slidesItems: ResourceItemDTO[] = useMemo(() => {
        const slidesBox = resourcesData?.find((resource) => resource.key === 'unitSlidesBox');
        const slidesItems = slidesBox
            ? slidesBox.items.filter((item) => {
                  return item.key && item.key.includes(`Unit${currentUnit}Slides`);
              })
            : [];
        return filterSlidesData(grade, slidesItems);
    }, [resourcesData, grade, currentUnit]);

    // Keep content rendered during close animation
    const [hasEverBeenOpen, setHasEverBeenOpen] = useState(isUnitSelected);
    const [lastSelectedTab, setLastSelectedTab] = useState(selectedTab);

    useEffect(() => {
        if (isUnitSelected) {
            setHasEverBeenOpen(true);
            if (selectedTab) {
                setLastSelectedTab(selectedTab);
            }
        }
    }, [isUnitSelected, selectedTab]);

    // Use lastSelectedTab when closing to keep content visible during animation
    const displayTab = isUnitSelected ? selectedTab : lastSelectedTab;

    if (hideUnit) {
        return null;
    }

    const isAnyLessonSelected = selectedLesson !== null;

    const header = (
        <div
            id={`G${grade}-unit${currentUnit}-header-div`}
            className={`unit background pd-G${grade}-primary ${isAnyLessonSelected ? 'disabled' : ''}`}
            onClick={() => {
                if (selectedLesson !== null) {
                    return;
                } else if (selectedUnit === currentUnit) {
                    setSelectedUnit(null);
                    setSelectedLesson(null);
                    setSelectedTab(null);
                } else {
                    setSelectedUnit(currentUnit);
                    setSelectedTab('lessons');
                }
            }}
        >
            <div className="unit-header">
                <span>
                    Unit {unit.unitNumber}: {unit.unitTitle}
                </span>
            </div>
            {!isAnyLessonSelected && (
                <DropdownArrow
                    id={`G${grade}_U${currentUnit}-dropdownArrow`}
                    rotate={isUnitSelected ? 'up' : 'down'}
                />
            )}
        </div>
    );

    const body = (
        <div
            id={`G${grade}-unit${currentUnit}-body-div`}
            className={`unit-content-wrapper ${isUnitSelected ? 'open' : ''}`}
        >
            <div className="lesson-container unit-content-inner">
                <div className="lesson-tabs">
                    <div className="lesson-tab-container">
                        <button
                            className={`lessons-tab tab pd-G${grade}-primary background ${displayTab === 'lessons' ? 'active' : ''}`}
                            data-tab="lessons"
                            onClick={() => {
                                if (selectedTab === 'lessons') {
                                    setSelectedTab(null);
                                } else {
                                    setSelectedTab('lessons');
                                }
                            }}
                        >
                            {translate('Lessons')}
                            <DropdownArrow
                                id={`G${grade}_U${currentUnit}-lessons-dropdownArrow`}
                                className="mobile-dropdown-arrow"
                                rotate={displayTab === 'lessons' ? 'up' : 'down'}
                                color="black"
                            />
                        </button>
                        <div
                            id={`unit${currentUnit}-lesson-container`}
                            className={`tab-content lessons-tab-content lessons mobile-lesson-contents ${displayTab === 'lessons' ? 'active' : ''}`}
                        >
                            {selectedUnit !== null && isAnyLessonSelected ? (
                                <SingleLesson
                                    unit={unit}
                                    grade={grade}
                                    currentUnit={currentUnit}
                                    selectedLesson={selectedLesson || 0}
                                    myLessons={myLessons}
                                    addMyLesson={addMyLesson}
                                    removeMyLesson={removeMyLesson}
                                />
                            ) : (
                                <LessonList
                                    unit={unit}
                                    grade={grade}
                                    currentUnit={currentUnit}
                                    slidesItems={slidesItems}
                                    myLessons={myLessons}
                                    setSelectedLesson={setSelectedLesson}
                                    addMyLesson={addMyLesson}
                                    removeMyLesson={removeMyLesson}
                                />
                            )}
                        </div>
                    </div>
                    <div className="lesson-tab-container">
                        <button
                            className={`assessments-tab tab pd-G${grade}-primary background ${displayTab === 'assessments' ? 'active' : ''}`}
                            data-tab="assessments"
                            onClick={() => {
                                if (selectedTab === 'assessments') {
                                    setSelectedTab(null);
                                } else {
                                    setSelectedTab('assessments');
                                }
                            }}
                        >
                            {translate('Assessments')}
                            <DropdownArrow
                                id={`G${grade}_U${currentUnit}-assessments-dropdownArrow`}
                                className="mobile-dropdown-arrow"
                                rotate={displayTab === 'assessments' ? 'up' : 'down'}
                                color="black"
                            />
                        </button>
                        <div
                            id={`G${grade}_U${currentUnit}-assessments-container`}
                            className={`tab-content assessments-tab-content assessments mobile-lesson-contents ${displayTab === 'assessments' ? 'active' : ''}`}
                        >
                            <AssessmentBoxes
                                grade={grade}
                                showTitle={false}
                                preventDataLoad
                                preloadedAssessmentsData={assessmentsData || []}
                            />
                        </div>
                    </div>
                    <div className="lesson-tab-container">
                        <button
                            className={`resources-tab tab pd-G${grade}-primary background ${displayTab === 'resources' ? 'active' : ''}`}
                            data-tab="resources"
                            onClick={() => {
                                if (selectedTab === 'resources') {
                                    setSelectedTab(null);
                                } else {
                                    setSelectedTab('resources');
                                }
                            }}
                        >
                            {translate('Resources')}
                            <DropdownArrow
                                id={`G${grade}_U${currentUnit}-resources-dropdownArrow`}
                                className="mobile-dropdown-arrow"
                                rotate={displayTab === 'resources' ? 'up' : 'down'}
                                color="black"
                            />
                        </button>
                        <div
                            id={`G${grade}_U${currentUnit}-resources-container`}
                            className={`tab-content resources-tab-content resources mobile-lesson-contents ${displayTab === 'resources' ? 'active' : ''}`}
                        >
                            <ResourceBoxes
                                resourcesData={resourcesData}
                                loading={loading}
                                error={error}
                            />
                        </div>
                    </div>
                </div>
                {/* DESKTOP TABS */}
                <div className="lesson-contents desktop-lesson-contents">
                    {hasEverBeenOpen && (
                        <>
                            <div
                                id={`unit${currentUnit}-lesson-container`}
                                className={`tab-content lessons-tab-content lessons ${displayTab === 'lessons' ? 'active' : ''}`}
                            >
                                {selectedUnit !== null && isAnyLessonSelected ? (
                                    <SingleLesson
                                        unit={unit}
                                        grade={grade}
                                        currentUnit={currentUnit}
                                        selectedLesson={selectedLesson || 0}
                                        myLessons={myLessons}
                                        addMyLesson={addMyLesson}
                                        removeMyLesson={removeMyLesson}
                                    />
                                ) : (
                                    <LessonList
                                        unit={unit}
                                        grade={grade}
                                        currentUnit={currentUnit}
                                        slidesItems={slidesItems}
                                        myLessons={myLessons}
                                        setSelectedLesson={setSelectedLesson}
                                        addMyLesson={addMyLesson}
                                        removeMyLesson={removeMyLesson}
                                    />
                                )}
                            </div>
                            <div
                                id={`G${grade}_U${currentUnit}-assessments-container`}
                                className={`tab-content assessments-tab-content assessments ${displayTab === 'assessments' ? 'active' : ''}`}
                            >
                                <AssessmentBoxes grade={grade} showTitle={false} />
                            </div>
                            <div
                                id={`G${grade}_U${currentUnit}-resources-container`}
                                className={`tab-content resources-tab-content resources ${displayTab === 'resources' ? 'active' : ''}`}
                            >
                                <ResourceBoxes
                                    resourcesData={resourcesData}
                                    loading={loading}
                                    error={error}
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
    return (
        <React.Fragment key={`allLessons-${grade}-${currentUnit}`}>
            {isAnyLessonSelected && (
                <LessonNav
                    unit={unit}
                    grade={grade}
                    selectedLesson={selectedLesson}
                    setSelectedLesson={setSelectedLesson}
                />
            )}
            {header}
            {body}
        </React.Fragment>
    );
};

export default CollapsibleUnit;
